import { FC, memo } from 'react';
import { isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { ApprovalRequest } from 'types';
// Import Components
import { Tooltip } from '@geneui/components';
import Countdown from 'components/common/Countdown';
import ApprovalStateTag from 'components/common/ApprovalStateTag';
import CampaignApprovalComment from './CampaignApprovalComment';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Helpers
import { customMomentWithoutTimezoneConversion } from 'services/Helpers';
// Import Constants
import { ApprovalRequestPriorityLabels, l, TimeZonePack } from 'constants/common';
// Import Enums
import { ApprovalRequestStateEnum } from 'enums';
// Import Styles
import styles from './campaignApprovalInfoModalBody.module.scss';

interface Props {
    approvalRequest: Nilable<ApprovalRequest>;
}

const CampaignApprovalInfoModalBody: FC<Props> = ({ approvalRequest }) => {
    const { t } = useTranslation();
    const { partnerDateTimeFormat } = usePartnerDateFormats();

    if (isNil(approvalRequest)) {
        return null;
    }

    const {
        ApprovalRequestId,
        State,
        Priority,
        Deadline,
        DeadlineTimeZone,
        AssignedToUserName,
        AssignedDate,
        CreatorName,
        CreatedDate,
        RequestMessage,
        RespondedByUserName,
        RespondedDate,
        ResponseMessage,
    } = approvalRequest;

    const getDateTime = (date: Nilable<string>) =>
        isEmpty(date) ? t(l.NA) : customMomentWithoutTimezoneConversion(date).format(partnerDateTimeFormat);

    // The deadline only counts down while the request is still waiting for a response
    const isCountdownVisible = State === ApprovalRequestStateEnum.Pending;
    return (
        <div className={`crm-campaign-info-detail-modal-body crm-scrollable-container ${styles.approvalInfo}`}>
            <div className="title-wrapper">
                <p className="title-wrapper-text">{t(l.ApprovalDetails)}</p>
                <span className="title-wrapper-line" />
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.ApprovalId)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{ApprovalRequestId}</span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.ApprovalState)}</span>
                <span className="kv-value">
                    <ApprovalStateTag approvalRequest={approvalRequest} />
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Priority)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {t(ApprovalRequestPriorityLabels[Priority])}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Deadline)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {getDateTime(Deadline)} {TimeZonePack[DeadlineTimeZone]}{' '}
                    {isCountdownVisible && (
                        <Tooltip text={t(l.TimeRemaining)}>
                            <span className={styles.countdown}>
                                (
                                <Countdown date={Deadline} timeZone={DeadlineTimeZone} />)
                            </span>
                        </Tooltip>
                    )}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.AssignedTo)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {isEmpty(AssignedToUserName) ? t(l.Unassigned) : AssignedToUserName}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.AssignedDate)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{getDateTime(AssignedDate)}</span>
            </div>

            <div className="title-wrapper">
                <p className="title-wrapper-text">{t(l.Request)}</p>
                <span className="title-wrapper-line" />
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.RequestedBy)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {isEmpty(CreatorName) ? t(l.NA) : CreatorName}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.RequestedDate)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{getDateTime(CreatedDate)}</span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.RequesterComment)}</span>
                <CampaignApprovalComment comment={RequestMessage} />
            </div>

            <div className="title-wrapper">
                <p className="title-wrapper-text">{t(l.Response)}</p>
                <span className="title-wrapper-line" />
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.RespondedBy)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {isEmpty(RespondedByUserName) ? t(l.NA) : RespondedByUserName}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.RespondedDate)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{getDateTime(RespondedDate)}</span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.ResponseComment)}</span>
                <CampaignApprovalComment comment={ResponseMessage} />
            </div>
        </div>
    );
};

export default memo(CampaignApprovalInfoModalBody);
